import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal as AntModal, Input, message, Select } from "antd";
import styled from "@emotion/styled";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
	PauseOutlined,
	CaretRightOutlined,
	StopOutlined,
} from "@ant-design/icons";
import queryKeys from "common/queryKey";

dayjs.extend(customParseFormat);

const TIMER_STATE = {
	IDLE: "idle",
	RUNNING: "running",
	PAUSED: "paused",
	STOPPED: "stopped",
};

// ─── Styled Components ────────────────────────────────────────────────────────

const TimeLogModalBody = styled.div`
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }
  .form-group {
    display: flex; flex-direction: column; gap: 6px;
    label {
      font-size: 13px; font-weight: 500; color: #333;
      .required { color: red; margin-left: 2px; }
    }
    .error-msg { font-size: 12px; color: #ff4d4f; margin-top: 2px; }
  }
  &:focus { border-color: #00c48c; }
  &.field-error { border-color: #ff4d4f; &:focus { border-color: #ff4d4f; } }
  .timer-row { display: flex; align-items: center; gap: 6px; flex-wrap: nowrap; }
  .time-input { width: 75px !important; flex-shrink: 0; }
  .quill-wrapper {
    .ql-toolbar.ql-snow {
      border: 1px solid #d9d9d9; border-bottom: none; border-radius: 6px 6px 0 0;
      padding: 6px 10px; background: #fafafa; font-family: inherit;
    }
    .ql-container.ql-snow {
      border: 1px solid #d9d9d9; border-radius: 0 0 3px 3px;
      font-family: inherit; font-size: 14px;
    }
    .ql-editor {
      min-height: 90px; padding: 10px; font-size: 14px; font-family: inherit;
      &.ql-blank::before { color: #bfbfbf; font-style: normal; }
    }
    .ql-container.ql-snow:focus-within { border-color: #00c48c; }
  }
`;

const FullDatePicker = styled(DatePicker)`
  && {
    width: 100%; height: 32px; border-radius: 6px; border: 1px solid #d9d9d9;
    box-shadow: none; padding: 4px 11px;
    &:hover { border-color: #d9d9d9; box-shadow: none; }
    &.ant-picker-focused { border-color: #00c48c; box-shadow: none; }
    &.field-error { border-color: #ff4d4f; }
    .ant-picker-input > input { font-size: 14px; color: #333; }
  }
`;

const TotalAmountInput = styled(Input)`
  && {
    background: #f5f5f5 !important; color: #555 !important;
    cursor: not-allowed; border: 1px solid #e0e0e0; border-radius: 2px;
    &:hover, &:focus { border-color: #e0e0e0 !important; box-shadow: none !important; }
    .ant-input-prefix { color: #888; }
  }
`;

const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; align-items: center; gap: 12px; margin-top: 24px;
  .warn-text { font-size: 12px; color: #ff4d4f; margin-right: 4px; }
  .spinner {
    width: 14px; height: 14px; border: 2px solid #777; border-top-color: transparent;
    border-radius: 50%; animation: btn-spin 0.65s linear infinite; flex-shrink: 0;
  }
  @keyframes btn-spin { to { transform: rotate(360deg); } }
`;

const StartTimerBtn = styled.button`
  display: flex; align-items: center; gap: 4px; background: #b2f0e3; border: none;
  border-radius: 20px; padding: 4px 10px; font-size: 12px; font-weight: 500; color: #3d3d5c;
  cursor: pointer; white-space: nowrap; font-family: inherit; transition: background 0.15s; flex-shrink: 0;
`;

const TimerBadge = styled.div`
  display: flex; align-items: center; gap: 4px; background: #3d3d5c; color: #fff;
  border-radius: 20px; padding: 4px 10px; font-size: 12px; font-weight: 600;
  letter-spacing: 0.5px; white-space: nowrap; flex-shrink: 0;
`;

const TimerActionBtn = styled.div`
  display: flex; align-items: center; gap: 3px; border: none; border-radius: 6px;
  padding: 4px 8px; font-size: 14px; font-weight: 500; cursor: pointer;
  white-space: nowrap; font-family: inherit; transition: opacity 0.15s; flex-shrink: 0;
`;

const iconStyle = {
	fontSize: "14px", display: "inline-flex", alignItems: "center", justifyContent: "center",
	width: "16px", height: "16px", borderRadius: "3px", marginRight: "4px",
};

const QUILL_MODULES = {
	toolbar: [
		["bold", "italic", "underline", "strike"],
		[{ list: "ordered" }, { list: "bullet" }],
		["link"], ["clean"],
	],
};
const QUILL_FORMATS = ["bold", "italic", "underline", "strike", "list", "bullet", "link"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimerDisplay(secs) {
	const m = String(Math.floor(secs / 60)).padStart(2, "0");
	const s = String(secs % 60).padStart(2, "0");
	return `${m}:${s}`;
}

function parseTimeSpent(value) {
	if (!value) return 0;
	if (value.includes(":")) {
		const parts = value.split(":").map(Number);
		if (parts.length === 2) {
			const [mm, ss] = parts;
			return (mm * 60 + ss) / 3600;
		}
		if (parts.length === 3) {
			const [hh, mm, ss] = parts;
			return hh + mm / 60 + ss / 3600;
		}
	}
	return parseFloat(value) || 0;
}

function timeToSeconds(time) {
	const parts = time.split(":").map(Number);
	if (parts.length === 2) {
		const [hh, mm] = parts;
		return hh * 3600 + mm * 60;
	}
	if (parts.length === 3) {
		const [hh, mm, ss] = parts;
		return hh * 3600 + mm * 60 + ss;
	}
	throw new Error("Invalid time format");
}

function computeTotalAmount(timeSpent, rate) {
	const hours = parseTimeSpent(timeSpent);
	const rateVal = parseFloat(rate) || 0;
	if (!hours || !rateVal) return "";
	return (hours * rateVal).toFixed(2);
}

const REQUIRED_FIELDS = {
	client: "Client", matter: "Matter", timeSpent: "Time Spent",
	dateOfLog: "Date of Log", category: "Category",
};

const INITIAL_ERRORS = { client: "", matter: "", timeSpent: "", dateOfLog: "", category: "" };

// ✅ INITIAL_FORM is outside the component, dateOfLog is null — never leaks dayjs() into edit mode
const INITIAL_FORM = {
	client: "", matter: "", timeSpent: "", dateOfLog: null,
	category: "", rate: "", description: "", localCurrencyAmount: "", totalAmount: "",
};

// ─── fetchMatterFinancials ────────────────────────────────────────────────────

async function fetchMatterFinancials(matterId, currentUserRate, currentUserId) {
	const { url } = queryKeys.getMatterById(matterId);
	const response = await CALL_FEATCH_API(url, "get");

	const matterData =
		response?.data ??
		response?.response?.data ??
		null;

	if (!matterData) {
		console.warn("[Rate Debug] fetchMatterFinancials: could not find matterData in response", response);
		return null;
	}

	const financialPlan = matterData.financial_resourcePlan || {};
	const conversionRate = parseFloat(financialPlan.conversionRate) || 1;
	const currency = financialPlan.currency || "USD";

	const billingPreferences = Array.isArray(matterData.billingPreferenceEmployee)
		? matterData.billingPreferenceEmployee
		: [];

	let rate = "";

	if (billingPreferences.length > 0) {
		const matchedBilling = billingPreferences.find(
			(b) => String(b.userId).trim().toLowerCase() === String(currentUserId).trim().toLowerCase()
		);
		if (matchedBilling?.rate != null && parseFloat(matchedBilling.rate) > 0) {
			rate = String(matchedBilling.rate);
		} else {
			rate = currentUserRate ? String(currentUserRate) : "";
		}
	} else {
		const resourceRates = Array.isArray(financialPlan.rates) ? financialPlan.rates : [];
		const matchedResource = resourceRates.find(
			(r) => String(r.userId).trim().toLowerCase() === String(currentUserId).trim().toLowerCase()
		);
		rate =
			matchedResource?.rate != null && parseFloat(matchedResource.rate) > 0
				? String(matchedResource.rate)
				: currentUserRate
					? String(currentUserRate)
					: "";
	}

	return { conversionRate, currency, rate };
}

// ─── TimeLogModal ─────────────────────────────────────────────────────────────

const TimeLogModal = ({ visible, onClose, editRecord }) => {
	const userInfo = useSelector((store) => get(store, "common.userInfo"));
	const userList = useSelector((store) => get(store, "common.userList", []));

	const currentUserRate =
		userList?.find((u) => u.userid === userInfo?.userid)?.chargeoutrate || "";

	const isEditMode = Boolean(editRecord) && !editRecord?._fromTimer;

	const [form, setForm] = useState(INITIAL_FORM);
	const [errors, setErrors] = useState(INITIAL_ERRORS);
	const [clients, setClients] = useState([]);
	const [allMatters, setAllMatters] = useState([]);
	const [filteredMatters, setFilteredMatters] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loadingClients, setLoadingClients] = useState(false);
	const [loadingCategories, setLoadingCategories] = useState(false);
	const [loadingMatterDetails, setLoadingMatterDetails] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [conversionRate, setConversionRate] = useState(1);
	const [currency, setCurrency] = useState("USD");
	const [timerState, setTimerState] = useState(TIMER_STATE.IDLE);
	const [elapsed, setElapsed] = useState(0);
	const intervalRef = useRef(null);
	const elapsedRef = useRef(0);
	const fromTimerSeededRef = useRef(false);
	const isInitializedRef = useRef(false);
	const [timelogratestatus, setTimelogratestatus] = useState("Chargeout rate");

	// ── Recompute totalAmount whenever timeSpent or rate changes
	useEffect(() => {
		const computed = computeTotalAmount(form.timeSpent, form.rate);
		if (!computed) return;
		setForm((prev) => ({ ...prev, totalAmount: computed }));
	}, [form.timeSpent, form.rate]);

	// ── Recompute localCurrencyAmount whenever totalAmount or conversionRate changes
	useEffect(() => {
		if (form.totalAmount && conversionRate) {
			const local = (parseFloat(form.totalAmount) / conversionRate).toFixed(2);
			setForm((prev) => ({ ...prev, localCurrencyAmount: local }));
		}
	}, [form.totalAmount, conversionRate]);

	const setField = (key, value) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (key in INITIAL_ERRORS) setErrors((prev) => ({ ...prev, [key]: "" }));
	};

	const handleClientChange = (clientId) => {
		setField("client", clientId);
		if (!clientId) {
			setFilteredMatters(allMatters);
			setClients(Object.values(allMatters.reduce((acc, item) => {
				if (!acc[item.clientid]) acc[item.clientid] = { id: item.clientid, name: item.clientname };
				return acc;
			}, {})));
			setField("matter", "");
			setConversionRate(1);
			setCurrency("USD");
			setForm((prev) => ({ ...prev, rate: "", localCurrencyAmount: "", totalAmount: "" }));
			return;
		}
		setFilteredMatters(allMatters.filter((m) => String(m.clientid) === String(clientId)));
		setField("matter", "");
	};

	const handleMatterChange = async (matterId) => {
		// ✅ When matter is cleared
		if (!matterId) {
			setFilteredMatters(allMatters);

			// restore all clients
			setClients(
				Object.values(
					allMatters.reduce((acc, item) => {
						if (!acc[item.clientid]) {
							acc[item.clientid] = {
								id: item.clientid,
								name: item.clientname,
							};
						}
						return acc;
					}, {})
				)
			);

			setForm((prev) => ({
				...prev,
				matter: "",
				client: "",
				rate: "",
				localCurrencyAmount: "",
				totalAmount: "",
			}));

			setConversionRate(1);
			setCurrency("USD");

			return;
		}

		const selectedMatter = allMatters.find(
			(m) => String(m.id) === String(matterId)
		);

		if (selectedMatter) {
			setClients([
				{
					id: selectedMatter.clientid,
					name: selectedMatter.clientname,
				},
			]);

			setFilteredMatters(
				allMatters.filter(
					(m) =>
						String(m.clientid) === String(selectedMatter.clientid)
				)
			);
		}

		setForm((prev) => ({
			...prev,
			matter: matterId,
			client: selectedMatter?.clientid ?? prev.client,
		}));

		setErrors((prev) => ({
			...prev,
			matter: "",
			client: "",
		}));

		try {
			setLoadingMatterDetails(true);

			const financials = await fetchMatterFinancials(
				matterId,
				currentUserRate,
				userInfo?.userid
			);

			if (financials) {
				setConversionRate(financials.conversionRate);
				setCurrency(financials.currency);
				setForm((prev) => ({ ...prev, rate: financials.rate }));
				setTimelogratestatus(financials.rate && financials.rate !== String(currentUserRate)
					? "Custom rate"
					: "Chargeout rate"
				);
			}
		} catch (err) {
			console.error("Failed to fetch matter details:", err);
		} finally {
			setLoadingMatterDetails(false);
		}
	};

	// ── Reset on close
	useEffect(() => {
		if (!visible) {
			fromTimerSeededRef.current = false;
			clearInterval(intervalRef.current);
			setTimerState(TIMER_STATE.IDLE);
			setElapsed(0);
			setForm(INITIAL_FORM);
			setErrors(INITIAL_ERRORS);
			setClients([]);
			setAllMatters([]);
			setFilteredMatters([]);
			setCategories([]);
			setConversionRate(1);
			setCurrency("USD");
			setTimelogratestatus("Chargeout rate");
		}
	}, [visible]);

	// ── Seed form on open
	useEffect(() => {
		if (!visible) { isInitializedRef.current = false; return; }
		if (isInitializedRef.current) return;

		// ── Timer seeded record
		if (editRecord?._fromTimer && !fromTimerSeededRef.current) {
			fromTimerSeededRef.current = true;
			const hours = parseFloat(editRecord.timespenthours) || 0;
			const totalSeconds = Math.round(hours * 3600);
			elapsedRef.current = totalSeconds;
			setElapsed(totalSeconds);
			setTimerState(TIMER_STATE.STOPPED);
			// ✅ Timer mode: use today's date
			setForm((prev) => ({ ...prev, ...INITIAL_FORM, dateOfLog: dayjs(), timeSpent: String(hours) }));
			setErrors(INITIAL_ERRORS);
			return;
		}

		if (fromTimerSeededRef.current) return;

		if (isEditMode && editRecord) {
			// ✅ Edit mode: seed dateOfLog strictly from the record
			setForm({
				client: "", matter: "", category: "",
				timeSpent:
					editRecord.timespenthours != null ? String(editRecord.timespenthours)
						: editRecord.timeSpent != null ? String(editRecord.timeSpent) : "",
				dateOfLog: editRecord.logdate
					? dayjs(editRecord.logdate)
					: editRecord.dateOfLog
						? dayjs(editRecord.dateOfLog)
						: dayjs(),
				rate:
					editRecord.rateperhour != null ? String(editRecord.rateperhour)
						: editRecord.rate != null ? String(editRecord.rate) : "",
				description: editRecord.description || "",
				totalAmount: editRecord.totalamount != null
					? parseFloat(editRecord.totalamount).toFixed(2) : "",
				localCurrencyAmount:
					editRecord.convertedamount != null
						? parseFloat(editRecord.convertedamount).toFixed(2)
						: editRecord.localcurrencyamount != null
							? parseFloat(editRecord.localcurrencyamount).toFixed(2)
							: "",
			});
		} else {
			// ✅ Create mode: inject today's date explicitly here
			setForm({
				...INITIAL_FORM,
				dateOfLog: dayjs(),
				rate: currentUserRate ? String(currentUserRate) : "",
			});
		}

		setErrors(INITIAL_ERRORS);
		setTimerState(TIMER_STATE.IDLE);
		setElapsed(0);
		isInitializedRef.current = true;
	}, [visible, editRecord]);

	// ── Fetch clients + matters from new combined API
	const fetchClientsAndMatters = useCallback(async () => {
		try {
			setLoadingClients(true);
			const { url } = queryKeys.getClientListForLogs(`?userid=${userInfo?.userid}`);
			const response = await CALL_FEATCH_API(url, "get");

			const resolvedData =
				Array.isArray(response?.data) ? response.data :
					Array.isArray(response?.response?.data) ? response.response.data :
						null;
			if (!resolvedData) {
				console.warn("[TimeLog] Could not find data array in response. Dropdown will be empty.");
				return { clientList: [], matterList: [] };
			}

			const today = dayjs().startOf("day");

			// ✅ FIX: a matter/client with NO booking window (both dates null)
			// has no date restriction at all and must always be included.
			// Previously this branch returned false, which silently dropped
			// every record whose actualBookedStartDate/EndDate were both null
			// — emptying the Client/Matter dropdowns even though the API
			// returned valid data.
			const dateFilteredData = resolvedData.filter((item) => {
				const start = item.actualBookedStartDate
					? dayjs(item.actualBookedStartDate).startOf("day")
					: null;
				const end = item.actualBookedEndDate
					? dayjs(item.actualBookedEndDate).startOf("day")
					: null;

				if (!start && !end) return true; // no booking window -> always show
				if (start && !end) return !today.isBefore(start);
				if (!start && end) return !today.isAfter(end);
				return !today.isBefore(start) && !today.isAfter(end);
			});

			const formattedMatters = dateFilteredData.map((item, index) => ({
				key: item?.matterId ?? item?.matterid ?? index,
				id: item?.matterId ?? item?.matterid,
				name: item?.matterName ?? item?.mattername,
				clientid: item?.clientId ?? item?.clientid,
				clientname: item?.clientName ?? item?.clientname,
			}));

			// Build unique clients list from filtered matters
			const formattedClients = Object.values(
				formattedMatters.reduce((acc, item) => {
					if (item.clientid && !acc[item.clientid]) {
						acc[item.clientid] = { id: item.clientid, name: item.clientname };
					}
					return acc;
				}, {})
			);

			setAllMatters(formattedMatters);
			setFilteredMatters(formattedMatters);
			setClients(formattedClients);

			return { clientList: formattedClients, matterList: formattedMatters };
		} catch (err) {
			console.error("Failed to fetch clients and matters:", err);
		} finally {
			setLoadingClients(false);
		}
		return { clientList: [], matterList: [] };
	}, [userInfo?.userid]);

	const fetchCategories = useCallback(async () => {
		try {
			setLoadingCategories(true);
			const response = await CALL_FEATCH_API("insyts-matter/gettimelogcategory", "get");
			if (response?.status === "SUCCESS" && response?.response?.status) {
				const formatted = (response.response.data || []).map((item, index) => ({
					key: item?.categoryid || index, id: item?.categoryid, name: item?.categorytype || "",
				}));
				setCategories(formatted);
				return formatted;
			}
		} catch (err) { console.error("Failed to fetch categories:", err); }
		finally { setLoadingCategories(false); }
		return [];
	}, []);

	useEffect(() => {
		if (!visible) return;
		const normalize = (val) => val?.toString().trim().toLowerCase();

		const loadAll = async () => {
			const [{ clientList, matterList }, categoryList] = await Promise.all([
				fetchClientsAndMatters(),
				fetchCategories(),
			]);

			if (editRecord?._fromTimer) return;
			if (!isEditMode || !editRecord) return;

			let resolvedMatterId = null;

			setForm((prev) => {
				let resolvedClient = prev.client;
				if (editRecord.clientid != null) {
					const found = clientList.find((c) => String(c.id) === String(editRecord.clientid));
					resolvedClient = found?.id ?? prev.client;
				} else if (editRecord.clientname || editRecord.client) {
					const found = clientList.find((c) => normalize(c.name) === normalize(editRecord.clientname || editRecord.client));
					resolvedClient = found?.id ?? prev.client;
				}

				let resolvedMatter = prev.matter;
				if (editRecord.matterid != null) {
					const found = matterList.find((m) => String(m.id) === String(editRecord.matterid));
					resolvedMatter = found?.id ?? prev.matter;
				} else if (editRecord.mattername || editRecord.matter) {
					const found = matterList.find((m) => normalize(m.name) === normalize(editRecord.mattername || editRecord.matter));
					resolvedMatter = found?.id ?? prev.matter;
				}
				resolvedMatterId = resolvedMatter;

				let resolvedCategory = prev.category;
				if (editRecord.categoryid != null) {
					const found = categoryList.find((c) => String(c.id) === String(editRecord.categoryid));
					resolvedCategory = found?.id ? String(found.id) : prev.category;
				} else if (editRecord.categoryname || editRecord.category) {
					const found = categoryList.find((c) => normalize(c.name) === normalize(editRecord.categoryname || editRecord.category));
					resolvedCategory = found?.id ? String(found.id) : prev.category;
				}

				if (resolvedClient) {
					const filtered = matterList.filter((m) => String(m.clientid) === String(resolvedClient));
					setFilteredMatters(filtered.length > 0 ? filtered : matterList);
				}

				// ✅ Only resolve client/matter/category — never touch dateOfLog here
				return { ...prev, client: resolvedClient, matter: resolvedMatter, category: resolvedCategory };
			});

			if (resolvedMatterId) {
				try {
					setLoadingMatterDetails(true);
					const financials = await fetchMatterFinancials(resolvedMatterId, currentUserRate, userInfo?.userid);
					if (financials) {
						setConversionRate(financials.conversionRate);
						setCurrency(financials.currency);
						setForm((prev) => ({ ...prev, rate: financials.rate }));
						setTimelogratestatus(financials.rate && financials.rate !== String(currentUserRate)
							? "Custom rate"
							: "Chargeout rate"
						);
					}
				} catch (err) {
					console.error("Failed to fetch matter details for edit mode:", err);
				} finally {
					setLoadingMatterDetails(false);
				}
			}
		};

		loadAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	const validate = () => {
		const newErrors = { ...INITIAL_ERRORS };
		let valid = true;
		Object.entries(REQUIRED_FIELDS).forEach(([key, label]) => {
			if (!form[key] || String(form[key]).trim() === "") {
				newErrors[key] = `${label} is required`;
				valid = false;
			}
		});
		if (form.timeSpent) {
			const isMMSS = /^\d{2}:\d{2}$/.test(form.timeSpent);
			const isHHMMSS = /^\d{2}:\d{2}:\d{2}$/.test(form.timeSpent);
			if (!isMMSS && !isHHMMSS && (isNaN(form.timeSpent) || parseFloat(form.timeSpent) <= 0)) {
				newErrors.timeSpent = "Enter a valid time greater than 0";
				valid = false;
			}
		}
		setErrors(newErrors);
		return valid;
	};

	useEffect(() => {
		clearInterval(intervalRef.current);
		if (timerState === TIMER_STATE.RUNNING) {
			intervalRef.current = setInterval(() => {
				setElapsed((prev) => { const next = prev + 1; elapsedRef.current = next; return next; });
			}, 1000);
		}
		return () => clearInterval(intervalRef.current);
	}, [timerState]);

	const handleStart = () => { elapsedRef.current = 0; setElapsed(0); setTimerState(TIMER_STATE.RUNNING); };
	const handlePause = () => setTimerState(TIMER_STATE.PAUSED);
	const handlePlay = () => setTimerState(TIMER_STATE.RUNNING);
	const handleStop = () => { setTimerState(TIMER_STATE.STOPPED); setField("timeSpent", formatTimerDisplay(elapsedRef.current)); };
	const handleResume = () => setTimerState(TIMER_STATE.RUNNING);

	const isTimerActive = timerState === TIMER_STATE.RUNNING || timerState === TIMER_STATE.PAUSED;

	const formatTimeToHHMMSS = (time) => {
		if (!time) return "00:00:00";

		// Handle HH:MM:SS format
		if (/^\d{1,2}:\d{2}:\d{2}$/.test(time)) {
			return time.split(":").map(p => String(Number(p)).padStart(2, "0")).join(":");
		}

		// Handle MM:SS format (timer output)
		if (/^\d{1,2}:\d{2}$/.test(time)) {
			const [mm, ss] = time.split(":").map(Number);
			const totalSeconds = mm * 60 + ss;
			const hh = Math.floor(totalSeconds / 3600);
			const mins = Math.floor((totalSeconds % 3600) / 60);
			const secs = totalSeconds % 60;
			return `${String(hh).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
		}

		// Handle decimal hours (e.g. 2.25 → 02:15:00)
		const hours = parseFloat(time);
		if (!isNaN(hours) && hours > 0) {
			const totalSeconds = Math.round(hours * 3600);
			const hh = Math.floor(totalSeconds / 3600);
			const mm = Math.floor((totalSeconds % 3600) / 60);
			const ss = totalSeconds % 60;
			return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
		}

		return "00:00:00";
	};

	const handleSubmit = async () => {
		if (isTimerActive || submitting) return;
		if (!validate()) return;

		const selectedClient = clients.find((c) => c.id === form.client);
		const selectedMatter = allMatters.find((m) => m.id === form.matter);
		const selectedCategory = categories.find((cat) => String(cat.id) === String(form.category));

		try {
			setSubmitting(true);
			if (isEditMode) {
				const payload = {
					id: String(editRecord.id || editRecord.timelogid || ""),
					clientid: form.client, clientname: selectedClient?.name || editRecord.clientname || "",
					matterid: form.matter, mattername: selectedMatter?.name || editRecord.mattername || "",
					timespenthours: formatTimeToHHMMSS(form.timeSpent),
					logdate: form.dateOfLog ? form.dateOfLog.format("YYYY-MM-DD") : "",
					categoryid: form.category || null, categoryname: selectedCategory?.name || editRecord.categoryname || "",
					rateperhour: parseFloat(form.rate) || 0, description: form.description,
					updatedby: userInfo?.userid || userInfo?.email || "",
					totalamount: form.totalAmount ? parseFloat(form.totalAmount) : 0,
					localcurrencyamount: form.localCurrencyAmount ? parseFloat(form.localCurrencyAmount) : 0,
					currency,
					convertedamount: form.localCurrencyAmount ? parseFloat(form.localCurrencyAmount) : 0,
					timelogratestatus
				};
				const response = await CALL_FEATCH_API("insyts-matter/updatetimelog", "post", payload);
				if (response?.status === "SUCCESS") { message.success("Time log updated successfully!"); onClose(); }
				else message.error("Failed to update time log. Please try again.");
			} else {
				const payload = {
					clientid: form.client, clientname: selectedClient?.name || "",
					matterid: selectedMatter?.id, mattername: selectedMatter?.name || "",
					timespenthours: formatTimeToHHMMSS(form.timeSpent),
					logdate: form.dateOfLog ? form.dateOfLog.format("YYYY-MM-DD") : "",
					rateperhour: parseFloat(form.rate) || 0, description: form.description,
					createdby: userInfo?.userid, categoryid: form.category || null,
					categoryname: selectedCategory?.name || "",
					totalamount: form.totalAmount ? parseFloat(form.totalAmount) : 0,
					localcurrencyamount: form.localCurrencyAmount ? parseFloat(form.localCurrencyAmount) : 0,
					currency,
					convertedamount: form.localCurrencyAmount ? parseFloat(form.localCurrencyAmount) : 0,
					timelogratestatus
				};
				const response = await CALL_FEATCH_API("insyts-matter/addtimelog", "post", payload);
				if (response?.status === "SUCCESS") { message.success("Time log created successfully!"); onClose(); }
				else message.error("Failed to create time log. Please try again.");
			}
		} catch (error) {
			message.error("Something went wrong. Please try again.");
			console.error("Time log submit error:", error);
		} finally { setSubmitting(false); }
	};

	const isSubmitDisabled = isTimerActive || submitting;

	const TimerIcon = () => (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="#3d3d5c">
			<path d="M15 1H9v2h6V1zm-3 4a9 9 0 100 18 9 9 0 000-18zm1 9H11V8h2v6z" />
		</svg>
	);

	const renderTimerControls = () => {
		if (isEditMode) return null;
		switch (timerState) {
			case TIMER_STATE.IDLE:
				return <StartTimerBtn onClick={handleStart}><TimerIcon />Start Timer</StartTimerBtn>;
			case TIMER_STATE.RUNNING:
				return (
					<>
						<TimerBadge>⏱ {formatTimerDisplay(elapsed)}</TimerBadge>
						<TimerActionBtn onClick={handlePause}>
							<span style={{ display: "flex", alignItems: "center", color: "#374151" }}>
								<PauseOutlined style={{ ...iconStyle, background: "#f59e0b", color: "#fff" }} />Pause
							</span>
						</TimerActionBtn>
						<TimerActionBtn onClick={handleStop}>
							<span style={{ display: "flex", alignItems: "center", color: "#374151" }}>
								<StopOutlined style={{ ...iconStyle, background: "#ef4444", color: "#fff" }} />Stop
							</span>
						</TimerActionBtn>
					</>
				);
			case TIMER_STATE.PAUSED:
				return (
					<>
						<TimerBadge>⏱ {formatTimerDisplay(elapsed)}</TimerBadge>
						<TimerActionBtn onClick={handlePlay}>
							<span style={{ display: "flex", alignItems: "center", color: "#374151" }}>
								<CaretRightOutlined style={{ ...iconStyle, background: "#0ea5e9", color: "#fff" }} />Play
							</span>
						</TimerActionBtn>
						<TimerActionBtn onClick={handleStop}>
							<span style={{ display: "flex", alignItems: "center", color: "#374151" }}>
								<StopOutlined style={{ ...iconStyle, background: "#ef4444", color: "#fff" }} />Stop
							</span>
						</TimerActionBtn>
					</>
				);
			case TIMER_STATE.STOPPED:
				return (
					<>
						<TimerBadge>⏱ {formatTimerDisplay(elapsed)}</TimerBadge>
						<TimerActionBtn onClick={handleResume}>
							<CaretRightOutlined style={{ ...iconStyle, background: "#0ea5e9", color: "#fff" }} /> Resume
						</TimerActionBtn>
					</>
				);
			default: return null;
		}
	};

	return (
		<AntModal title={isEditMode ? "Edit Time Log" : "Creating Time Log"} open={visible} onCancel={onClose} footer={null} width={700}>
			<TimeLogModalBody>

				<div className="form-row">
					<div className="form-group">
						<label>Client<span className="required">*</span></label>
						<Select
							value={form.client || undefined} allowClear placeholder={loadingClients ? "Loading..." : "Select client"}
							onChange={handleClientChange} disabled={loadingClients} style={{ width: "100%" }}
							className={errors.client ? "field-error" : ""}
							options={clients.map((c) => ({ label: c.name, value: c.id }))} showSearch
							filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
						/>
						{errors.client && <span className="error-msg">{errors.client}</span>}
					</div>
					<div className="form-group">
						<label>Matter<span className="required">*</span></label>
						<Select
							value={form.matter || undefined} allowClear placeholder={loadingClients ? "Loading..." : "Select matter"}
							onChange={handleMatterChange} disabled={loadingClients} style={{ width: "100%" }}
							className={errors.matter ? "field-error" : ""}
							options={filteredMatters.map((m) => ({ label: m.name, value: m.id }))} showSearch
							filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
						/>
						{errors.matter && <span className="error-msg">{errors.matter}</span>}
					</div>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label>Time Spent (Hrs)<span className="required">*</span></label>
						<div className="timer-row">
							<Input
								type="text" className={`time-input${errors.timeSpent ? " field-error" : ""}`} placeholder="0.00"
								value={
									isEditMode ? form.timeSpent
										: (timerState === TIMER_STATE.RUNNING || timerState === TIMER_STATE.PAUSED) ? ""
											: form.timeSpent
								}
								onChange={(e) => {
									if (isEditMode || timerState === TIMER_STATE.IDLE || timerState === TIMER_STATE.STOPPED)
										setField("timeSpent", e.target.value);
								}}
								readOnly={!isEditMode && (timerState === TIMER_STATE.RUNNING || timerState === TIMER_STATE.PAUSED)}
								style={{ background: !isEditMode && (timerState === TIMER_STATE.RUNNING || timerState === TIMER_STATE.PAUSED) ? "#f5f5f5" : "#fff" }}
							/>
							{renderTimerControls()}
						</div>
						{errors.timeSpent && <span className="error-msg">{errors.timeSpent}</span>}
					</div>
					<div className="form-group">
						<label>Date of Log<span className="required">*</span></label>
						<FullDatePicker
							value={form.dateOfLog} onChange={(val) => setField("dateOfLog", val)}
							format="DD MMM YYYY" placeholder="Select date"
							className={errors.dateOfLog ? "field-error" : ""} allowClear={false}
						/>
						{errors.dateOfLog && <span className="error-msg">{errors.dateOfLog}</span>}
					</div>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label>Category<span className="required">*</span></label>
						<Select
							value={form.category || undefined} placeholder={loadingCategories ? "Loading..." : "Select category"}
							onChange={(val) => setField("category", val)} disabled={loadingCategories}
							style={{ width: "100%" }} className={errors.category ? "field-error" : ""}
							options={categories.map((cat) => ({ label: cat.name, value: String(cat.id) }))}
						/>
						{errors.category && <span className="error-msg">{errors.category}</span>}
					</div>
					<div className="form-row" style={{ marginBottom: 0 }}>
						<div className="form-group">
							<label>
								Rate (USD/Hr)
								{loadingMatterDetails && <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 6 }}>Loading...</span>}
							</label>
							<TotalAmountInput readOnly tabIndex={-1} prefix={<span style={{ color: "#888", marginRight: 2 }}>$</span>} value={form.rate || "0"} placeholder="0" />
						</div>
						<div className="form-group">
							<label>Total Amount (USD)</label>
							<TotalAmountInput readOnly tabIndex={-1} prefix={<span style={{ color: "#888", marginRight: 2 }}>$</span>} value={form.totalAmount || "0.00"} placeholder="0.00" />
						</div>
					</div>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label>
							Amount in Local Currency
							{currency && <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>({currency})</span>}
							{loadingMatterDetails && <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 6 }}>Loading...</span>}
						</label>
						<TotalAmountInput readOnly tabIndex={-1} value={form.localCurrencyAmount || "0.00"} placeholder="0.00" />
					</div>
					<div />
				</div>

				<div className="form-group" style={{ marginBottom: 4 }}>
					<label>Log Description</label>
					<div className="quill-wrapper">
						<ReactQuill
							key={editRecord?.timelogid || editRecord?.id || "new"} theme="snow"
							value={form.description} onChange={(value) => setField("description", value)}
							modules={QUILL_MODULES} formats={QUILL_FORMATS} placeholder="Enter description..."
						/>
					</div>
				</div>

				<ModalFooter>
					{isTimerActive && <span className="warn-text">⚠ Please stop the timer before submitting.</span>}
					<ButtonOutlined onClick={onClose} disabled={submitting}>Cancel</ButtonOutlined>
					<ButtonFilled onClick={handleSubmit} disabled={isSubmitDisabled} title={isTimerActive ? "Stop the timer before submitting" : ""}>
						{submitting && <span className="spinner" />}
						{submitting ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Create")}
					</ButtonFilled>
				</ModalFooter>

			</TimeLogModalBody>
		</AntModal>
	);
};

export default TimeLogModal;